import React from 'react';
import * as _ from 'lodash';

import {
  JOINING_FLAG,
  PREFECTURE_GROUPS, // 都道府県
  LEGAL_PERSONALITIES, // 法人格
  ONE_MASTER_FLAG, // 個人事業主
  PARTNER_COMPANY_PERSONAL_TYPE,
  CORPORATION_PERSONAL_NAME,
  CORPORATION_PERSONAL_KANA,
  ZENDESK_URL_GUIDE,
} from '../../utilities/constants';

import { Tel } from '../Tel'; // 電話番号入力フィールド
import { PostalCode } from '../PostalCode'; // 郵便番号入力フィールド
import styles from '../../styles/componentStyle/entrance/partner-company-register-form.scss';
import {
  convertFlagToCircleOrCross,
  isNullOrEmpty,
} from '../../utilities/stringUtil';
import { UserSerchDropDown } from '../dropdowns/single/UserSerch';
import CompanyLogo from '../image/CompanyLogo';
import { FormElement, FormListItem } from '../CommonFormComponents';
import { ArrowDownIcon } from '../icons/ArrowDownIcon';
import { CcusLineComponent } from '../CcusFormComponent';
import { RadioButtonIcon } from '../icons/RadioButtonIcon';
import { Description } from '../Description';
import {
  getLabelPersonalCompanyType,
  isOneOwnerCompany,
} from '../../utilities/partnerCompanyUtil';
import { messageUtility } from '../../utilities/messageUtility';

const TextInput = props => {
  if (props.disabled) {
    return <span className={props.className}>{props.value}</span>;
  }
  return <input {...props} />;
};

/**
 * 協力会社詳細・編集 画面
 * 画面遷移
 *  1.  本社管理者でログイン > 協力会社一覧 > 確認ボタン
 *  2.  協力会社管理者 > 会社情報確認 > 基本情報
 *  3.  協力会社管理者 > 代行登録会社一覧 > 確認ボタン
 * @param values {object} 入力値
 * @param events {func} イベントハンドラ
 * @param messages {object} バリデーションメッセージ
 * @param image {string} 画像パス
 * @param readOnly {boolean} プレビュー表示
 * @param ccusCompanyData CCUS事業者情報
 * @param isPartnerCompanyBranchExist {boolean}
 * @param totalWorkers {number}
 */
export const PartnerCompanyForm = ({
  values,
  users,
  events,
  messages,
  image,
  readOnly = false,
  ccusCompanyData,
  onUserSave,
  userCompany,
  isCommonHojin,
  isPartnerCompanyBranchExist,
  totalWorkers,
}) => {
  const safetyDocItemFlag =
    values.joining_flag === JOINING_FLAG.commonPartnerCompany.id;

  const isPerson =
    values.one_master_flag === ONE_MASTER_FLAG.personal.id ? true : false;

  // 職長パスワードリセット通知先表示ロジック
  const passWardChange = () => {
    if (readOnly) {
      return _.map(users, user =>
        user.password_reset_notice_flag ? (
          <p key={user.id} className='form-list__element--break'>
            {`${user.name_sei} ${user.name_mei}`}
          </p>
        ) : null
      );
    }
    return (
      <UserSerchDropDown
        selectedValues={users}
        userCompany={userCompany}
        onSelect={onUserSave}
        isHideHiddenItem={true}
      />
    );
  };

  const ccusElement = (_ccusCompanyData, section, key) => {
    if (!_ccusCompanyData || isNullOrEmpty(_ccusCompanyData[section][key])) {
      return <div className={styles['ccus-preview-value']} />;
    }

    let label = _ccusCompanyData[section][key];

    //法人格
    if (key === 'juridical_personality_code') {
      label = _.filter(LEGAL_PERSONALITIES, item => {
        return item.id === _ccusCompanyData[section][key];
      })[0].name;
    }

    //都道府県
    if (key === 'prefectures_code_id') {
      _.map(PREFECTURE_GROUPS, region =>
        _.map(region.prefectures, prefecture => {
          if (prefecture.id === _ccusCompanyData[section][key]) {
            label = prefecture.name;
          }
        })
      );
    }
    return (
      <CcusLineComponent
        onClick={() => {
          events.setCompanyByCcus(key, section, _ccusCompanyData[section][key]);
        }}
      >
        {label}
      </CcusLineComponent>
    );
  };

  const renderDescriptionOneOwnerCompany = () => {
    const isOneOwnerCompanyHasMultipleWorker =
      isOneOwnerCompany(values.one_owner_flag) &&
      !_.isNull(totalWorkers) &&
      totalWorkers > 1;
    let messages = '';
    if (isOneOwnerCompanyHasMultipleWorker) {
      messages = messageUtility.getMessage('W-001-303');
    } else if (isPartnerCompanyBranchExist) {
      messages = messageUtility.getMessage('W-001-304');
    }
    return (
      !!messages && (
        <Description
          type='primary'
          style={{ marginTop: 8, whiteSpace: 'pre-line' }}
        >
          {messages}
        </Description>
      )
    );
  };

  const renderOneOwnerCompany = () => {
    if (readOnly) {
      return (
        <TextInput
          id='one_owner_flag'
          type='text'
          name='one_owner_flag'
          disabled={true}
          value={getLabelPersonalCompanyType(values.one_owner_flag)}
        />
      );
    }

    const isDisabled = value =>
      isPartnerCompanyBranchExist && isOneOwnerCompany(value);

    return (
      <div className='form-list-radio'>
        {_.map(
          Object.values(PARTNER_COMPANY_PERSONAL_TYPE),
          ({ value, label }) => (
            <div className='form-list-radio__item' key={value}>
              <label className='radio'>
                <input
                  type='radio'
                  name='one_owner_flag'
                  id={`one_owner_flag${value}`}
                  value={value}
                  className={isDisabled(value) ? 'radio-disabled' : ''}
                  checked={_.isEqual(value, values.one_owner_flag)}
                  onChange={e => events.setCompany(e)}
                  disabled={isDisabled(value)}
                />
                <RadioButtonIcon className='icon--2x' />
                <span
                  className={isDisabled(value) ? 'label-radio--disabled' : ''}
                >
                  {label}
                </span>
              </label>
            </div>
          )
        )}
      </div>
    );
  };

  return (
    <React.Fragment>
      <FormListItem label='種別'>
        <FormElement>
          <TextInput
            id='company_type'
            type='text'
            disabled={true}
            value={
              isPerson
                ? ONE_MASTER_FLAG.personal.name
                : isCommonHojin
                ? ONE_MASTER_FLAG.corporation.name
                : ''
            }
            className={
              isPerson
                ? 'company-type--personal'
                : isCommonHojin
                ? 'company-type--corporation'
                : ''
            }
          />
          {!readOnly && (
            <div>
              ※会社種別の変更については
              <a
                className='clickable-text'
                href={ZENDESK_URL_GUIDE.COMPANY_TYPE}
                target='_blank'
                rel='noreferrer noopener'
              >
                こちら
              </a>
              をご確認ください。
            </div>
          )}
        </FormElement>
      </FormListItem>
      {isPerson && (
        <FormListItem
          label={messageUtility.getMessage('I-000-186')}
          required={!readOnly}
          isAlignStart={true}
        >
          <FormElement
            validate={messages.one_owner_flag}
            additionalElement={renderDescriptionOneOwnerCompany()}
          >
            {renderOneOwnerCompany()}
          </FormElement>
        </FormListItem>
      )}
      <FormListItem
        label={
          isPerson
            ? CORPORATION_PERSONAL_NAME.personal_name
            : CORPORATION_PERSONAL_NAME.corporation_name
        }
        safetyDocItem={safetyDocItemFlag}
        required={!readOnly}
      >
        <FormElement
          validate={messages.name}
          ccusComponent={ccusElement(ccusCompanyData, 'company', 'name')}
        >
          <TextInput
            id='company_name'
            type='text'
            name='name'
            disabled={readOnly}
            value={values.name}
            maxLength={255}
            onChange={e => events.setCompany(e)}
            onBlur={events.validate ? e => events.validate(e) : null}
          />
        </FormElement>
      </FormListItem>
      <FormListItem
        label={
          isPerson
            ? CORPORATION_PERSONAL_KANA.personal_kana
            : CORPORATION_PERSONAL_KANA.corporation_kana
        }
        required={!readOnly}
      >
        <FormElement
          validate={messages.kana}
          ccusComponent={ccusElement(ccusCompanyData, 'company', 'kana')}
        >
          <TextInput
            id='company_kana'
            type='text'
            name='kana'
            disabled={readOnly}
            value={values.kana}
            maxLength={512}
            onChange={e => events.setCompany(e)}
            onBlur={events.validate ? e => events.validate(e) : null}
          />
        </FormElement>
      </FormListItem>
      {isCommonHojin === true && (
        <FormListItem label='法人格' required={!readOnly}>
          <FormElement validate={messages.juridical_personality_code}>
            <select
              name='juridical_personality_code'
              disabled={readOnly}
              value={_.defaultTo(values.juridical_personality_code, '')}
              onChange={e => events.setCompany(e)}
              onBlur={events.validate ? e => events.validate(e) : null}
            >
              <option value='' disabled hidden>
                選択してください
              </option>
              {_.map(LEGAL_PERSONALITIES, (personality, index) => (
                <option key={index} value={personality.id}>
                  {personality.name}
                </option>
              ))}
            </select>
            <ArrowDownIcon className='icon--2_4x form-list__arrow-icon' />
          </FormElement>
        </FormListItem>
      )}
      {isCommonHojin && (
        <FormListItem label='法人番号'>
          <FormElement>
            <TextInput
              id='company_number'
              type='text'
              disabled={true}
              value={values.company_number}
            />
          </FormElement>
        </FormListItem>
      )}
      {/* 元請固有(joining_flagが0)の場合、支店名と支店名ふりがなを表示 */}
      {values.joining_flag === JOINING_FLAG.contractorInherent.id ? (
        <React.Fragment>
          <FormListItem label='支店名'>
            <FormElement validate={messages.company_branch_name}>
              <TextInput
                id='company_branch_name'
                type='text'
                name='name'
                disabled={readOnly}
                value={values.company_branch.name}
                maxLength={255}
                onChange={e =>
                  events.setValue('name', e.target.value, 'company_branch')
                }
                onBlur={events.validate ? e => events.validate(e) : null}
              />
            </FormElement>
          </FormListItem>
          <FormListItem label='支店名ふりがな'>
            <FormElement validate={messages.company_branch_kana}>
              <TextInput
                id='company_branch_kana'
                type='text'
                name='kana'
                disabled={readOnly}
                value={values.company_branch.kana}
                maxLength={512}
                onChange={e =>
                  events.setValue('kana', e.target.value, 'company_branch')
                }
                onBlur={events.validate ? e => events.validate(e) : null}
              />
            </FormElement>
          </FormListItem>
        </React.Fragment>
      ) : null}
      <FormListItem
        label='郵便番号'
        safetyDocItem={safetyDocItemFlag}
        required={!readOnly}
      >
        <PostalCode
          value={values.postal_code}
          handleOnChange={events.setValue}
          setKey='postal_code'
          readOnly={readOnly}
          error={messages.postal_code}
          validate={events.validate}
          ccusComponent={ccusElement(ccusCompanyData, 'company', 'postal_code')}
        />
      </FormListItem>
      <FormListItem
        label='都道府県'
        safetyDocItem={safetyDocItemFlag}
        required={!readOnly}
      >
        <FormElement
          validate={messages.prefectures_code_id}
          ccusComponent={ccusElement(
            ccusCompanyData,
            'company',
            'prefectures_code_id'
          )}
        >
          <select
            name='prefectures_code_id'
            disabled={readOnly}
            value={values.prefectures_code_id}
            onChange={e =>
              events.setValue('prefectures_code_id', e.target.value)
            }
            onBlur={events.validate ? e => events.validate(e) : null}
          >
            <option value=''>選択してください</option>
            {_.map(PREFECTURE_GROUPS, (region, index) => (
              <optgroup key={index} label={region.label}>
                {_.map(region.prefectures, prefecture => (
                  <option key={prefecture.id} value={prefecture.id}>
                    {prefecture.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <ArrowDownIcon className='icon--2_4x form-list__arrow-icon' />
        </FormElement>
      </FormListItem>
      <FormListItem
        label='市区町村'
        safetyDocItem={safetyDocItemFlag}
        required={!readOnly}
      >
        <FormElement
          validate={messages.city_name}
          ccusComponent={ccusElement(ccusCompanyData, 'company', 'city_name')}
        >
          <TextInput
            type='text'
            name='city_name'
            disabled={readOnly}
            value={values.city_name}
            placeholder={messageUtility.getMessage('I-000-190')}
            maxLength={512}
            onChange={e => events.setValue('city_name', e.target.value)}
            onBlur={events.validate ? e => events.validate(e) : null}
          />
        </FormElement>
      </FormListItem>
      <FormListItem
        label={messageUtility.getMessage('I-000-188')}
        safetyDocItem={safetyDocItemFlag}
      >
        <FormElement
          validate={messages.street_number}
          ccusComponent={ccusElement(
            ccusCompanyData,
            'company',
            'street_number'
          )}
        >
          <TextInput
            type='text'
            name='street_number'
            disabled={readOnly}
            value={_.defaultTo(values.street_number, '')}
            placeholder={messageUtility.getMessage('I-000-191')}
            maxLength={512}
            onChange={e => events.setValue('street_number', e.target.value)}
            onBlur={events.validate ? e => events.validate(e) : null}
          />
        </FormElement>
      </FormListItem>
      <FormListItem
        label='建物名・部屋番号等'
        safetyDocItem={safetyDocItemFlag}
      >
        <FormElement
          validate={messages.building_name}
          ccusComponent={ccusElement(
            ccusCompanyData,
            'company',
            'building_name'
          )}
        >
          <TextInput
            type='text'
            name='building_name'
            disabled={readOnly}
            value={_.defaultTo(values.building_name, '')}
            placeholder='（例）赤坂ビルディング'
            maxLength={512}
            onChange={e => events.setValue('building_name', e.target.value)}
            onBlur={events.validate ? e => events.validate(e) : null}
          />
        </FormElement>
      </FormListItem>
      <FormListItem
        label='電話番号'
        safetyDocItem={safetyDocItemFlag}
        required={!readOnly}
      >
        <Tel
          value={values.tel}
          handleOnChange={events.setValue}
          setKey='tel'
          readOnly={readOnly}
          error={messages.tel}
          validate={e => events.validate(e, 'tel')}
          ccusComponent={ccusElement(ccusCompanyData, 'company', 'tel')}
        />
      </FormListItem>
      <FormListItem label='FAX番号'>
        <Tel
          value={values.fax}
          readOnly={readOnly}
          handleOnChange={events.setValue}
          setKey='fax'
          error={messages.fax}
          validate={e => events.validate(e, 'fax')}
        />
      </FormListItem>
      <CompanyLogo
        readOnly={readOnly}
        errorMessage={messages.image}
        image={image}
        onFileUpDate={events.setImage}
        onFileDelete={events.deleteImage}
      />
      <FormListItem label='共通化状態'>
        <FormElement>
          {/* Buildee利用のjoining_flagを文字に変換 */}
          {convertFlagToCircleOrCross(String(values.joining_flag))}
        </FormElement>
      </FormListItem>

      <FormListItem label='職長パスワードリセット通知先'>
        <FormElement validate={messages.juridical_personality_code}>
          {passWardChange()}
        </FormElement>
      </FormListItem>

      <FormListItem label='更新ユーザー'>
        <FormElement>{values.update_username}</FormElement>
      </FormListItem>

      <FormListItem label='更新日時'>
        <FormElement>{values.updated_at}</FormElement>
      </FormListItem>
    </React.Fragment>
  );
};



// WEBPACK FOOTER //
// ./src/components/forms/PartnerCompany.jsx